// src/services/spotifyArtistService.ts
import axios from 'axios';
import { API_CONFIG, getApiUrl } from '../config/api';
import { getCurrentUserId, isAuthenticated, getAccessToken, refreshAccessToken } from './authService';

export interface SpotifyArtist {
  id: string;
  name: string;
  images: Array<{
    url: string;
    height: number;
    width: number;
  }>;
  followers: {
    total: number;
  };
  genres: string[];
  popularity: number;
}

export interface SearchResponse {
  artists: {
    items: SpotifyArtist[];
    total: number;
    limit: number;
    offset: number;
  }
}

// Interface für die API-Antwort für verknüpfte Künstler
interface ArtistDetailsResponse {
  artist: {
    id: number;
    spotifyId: string;
    name: string;
    genre: string;
    score: number;
    // ...weitere Properties
  };
  scoreHistory: any[];
  releases: any[];
  songs: any[];
  monthlyMetrics: any[];
}

/**
 * Sucht nach Künstlern mit dem angegebenen Suchbegriff
 */
export const searchArtists = async (query: string, limit: number = 10): Promise<SpotifyArtist[]> => {
  try {
    const token = getAccessToken();
    if (!token) {
      console.log("Kein Access-Token für die Suche verfügbar");
      return [];
    }

    const response = await axios.get<SearchResponse>(
      getApiUrl(API_CONFIG.ENDPOINTS.SPOTIFY.SEARCH_ARTISTS), 
      {
        params: { q: query, limit },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    return response.data?.artists?.items || [];
  } catch (error) {
    console.error('Error searching for artists:', error);
    
    // Bei 401 Fehler versuchen wir das Token zu aktualisieren und erneut zu suchen
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      try {
        const refreshSuccess = await refreshAccessToken();
        if (refreshSuccess) {
          // Erneuter Versuch mit neuem Token
          const newToken = getAccessToken();
          if (newToken) {
            const retryResponse = await axios.get<SearchResponse>(
              getApiUrl(API_CONFIG.ENDPOINTS.SPOTIFY.SEARCH_ARTISTS), 
              {
                params: { q: query, limit },
                headers: {
                  'Authorization': `Bearer ${newToken}`
                }
              }
            );
            return retryResponse.data?.artists?.items || [];
          }
        }
      } catch (refreshError) {
        console.error("Fehler bei Token-Aktualisierung nach Suche:", refreshError);
      }
    }
    
    return [];
  }
};

/**
 * Verknüpft einen Spotify-Künstler mit dem aktuellen Benutzer
 */
export const linkArtistToUser = async (spotifyId: string, name: string, genre: string = ''): Promise<boolean> => {
  console.log("linkArtistToUser called with:", { spotifyId, name, genre });
  const retryCount = 3; // Anzahl der Versuche
  
  for (let attempt = 0; attempt < retryCount; attempt++) {
    try {
      // Prüfen ob der Benutzer angemeldet ist
      if (!isAuthenticated()) {
        console.error('User is not authenticated');
        return false;
      }
      
      // Mit Retry-Logik
      console.log(`Versuche Artist zu verknüpfen (Versuch ${attempt + 1}/${retryCount})`);
      
      const response = await axios.post(
        getApiUrl(API_CONFIG.ENDPOINTS.ARTIST.LINK('me')),
        { spotifyId, name, genre }
      );
      
      if (response.data && response.data.success) {
        console.log("Artist erfolgreich verknüpft:", response.data);
        return true;
      }
      
      console.log("Artist-Verknüpfung fehlgeschlagen, Antwort:", response.data);
      
    } catch (error) {
      console.error(`Error linking artist to user (attempt ${attempt + 1}/${retryCount}):`, error);
      
      // Bei 401 Fehler versuchen wir das Token zu aktualisieren und erneut zu verknüpfen
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        try {
          const refreshSuccess = await refreshAccessToken();
          if (!refreshSuccess) {
            console.log("Token-Aktualisierung fehlgeschlagen, Abbruch der Verknüpfung");
            return false; // Wenn Token-Aktualisierung fehlschlägt, weitere Versuche abbrechen
          }
          // Bei Erfolg weiter mit der nächsten Iteration
          continue;
        } catch (refreshError) {
          console.error("Fehler bei Token-Aktualisierung nach Verknüpfungsfehler:", refreshError);
          return false;
        }
      }
      
      // Bei nicht-Auth-Fehlern warten wir kurz zwischen den Versuchen
      if (attempt < retryCount - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      } else {
        return false; // Nach allen Versuchen aufgeben
      }
    }
  }
  
  return false; // Wenn wir hier ankommen, waren alle Versuche erfolglos
};

/**
 * Hebt die Verknüpfung eines Künstlers mit dem aktuellen Benutzer auf
 */
export const unlinkArtistFromUser = async (artistId: string): Promise<boolean> => {
  try {
    // Prüfen ob der Benutzer angemeldet ist
    if (!isAuthenticated()) {
      console.error('User is not authenticated');
      return false;
    }
    
    const response = await axios.delete(
      getApiUrl(API_CONFIG.ENDPOINTS.ARTIST.UNLINK(artistId))
    );
    
    return response.data && response.data.success;
  } catch (error) {
    console.error('Error unlinking artist:', error);
    return false;
  }
};

/**
 * Ruft die mit dem aktuellen Benutzer verknüpften Künstler ab
 */
export const getUserLinkedArtists = async (): Promise<{id: string, spotifyId: string, name: string}[]> => {
  const retryCount = 2; // Anzahl der Versuche
  
  for (let attempt = 0; attempt < retryCount; attempt++) {
    try {
      // Prüfen, ob der Nutzer authentifiziert ist
      if (!isAuthenticated()) {
        console.error('User is not authenticated');
        return [];
      }
      
      // Mit Retry-Logik
      const response = await axios.get(
        getApiUrl(API_CONFIG.ENDPOINTS.ARTIST.LINKED_ARTISTS)
      );
      
      console.log("API-Antwort für verknüpfte Künstler:", response.data);
      
      // Fall 1: Die Antwort ist ein Array von Künstlern (wie erwartet)
      if (Array.isArray(response.data)) {
        return response.data.map(artist => ({
          id: artist.id || '',
          spotifyId: artist.spotifyId || artist.spotify_id || '', // Handle different property names
          name: artist.name || ''
        }));
      }
      
      // Fall 2: Die Antwort ist ein einzelnes Künstler-Objekt mit Details
      if (response.data && response.data.artist) {
        const artistData = response.data.artist;
        return [{
          id: String(artistData.id) || '',
          spotifyId: artistData.spotifyId || '',
          name: artistData.name || 'Unknown Artist'
        }];
      }
      
      // Fall 3: Die Antwort hat keine erkennbare Struktur
      console.warn("Unbekanntes Antwortformat:", response.data);
      return [];
      
    } catch (error) {
      console.error(`Error fetching linked artists (attempt ${attempt + 1}/${retryCount}):`, error);
      
      // Bei 401 Fehler versuchen wir das Token zu aktualisieren
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        try {
          const refreshSuccess = await refreshAccessToken();
          if (!refreshSuccess) {
            console.log("Token-Aktualisierung fehlgeschlagen, Abbruch der Abfrage");
            return []; 
          }
          // Bei Erfolg weiter mit der nächsten Iteration
          continue;
        } catch (refreshError) {
          console.error("Fehler bei Token-Aktualisierung nach Linked-Artists-Fehler:", refreshError);
          return [];
        }
      }
      
      // Bei nicht-Auth-Fehlern warten wir kurz zwischen den Versuchen
      if (attempt < retryCount - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        return []; // Nach allen Versuchen aufgeben
      }
    }
  }
  
  // Fallback: Dummy-Artist, um UI-Tests zu ermöglichen
  if (process.env.NODE_ENV === 'development') {
    console.log("⚠️ ENTWICKLUNGSMODUS: Gebe Test-Artist zurück, weil keine echten gefunden wurden");
    return [{
      id: 'test-id',
      spotifyId: 'test-spotify-id',
      name: 'Test Artist (Dev Mode)'
    }];
  }
  
  return []; // Wenn wir hier ankommen, waren alle Versuche erfolglos
};